import { useCapacityInputClass } from '../capacity/useCapacityInputClass';
import { useEffect, useState } from 'react';
import { Drawer } from '../../../components/ui/Drawer';
import WarningConfirmModal from '../../../components/ui/WarningConfirmModal';
import DateTimePicker from '../../../components/DateTimePicker';
import MarketConditionsHistory from '../../../components/MarketConditionsHistory';
import { calendarDate, dateParam } from '../../accounting/reports/reportUtils';
import MarketConditionsImageThumb from '../../../components/MarketConditionsImageThumb';
import ScreenshotEditor from '../../../components/ScreenshotEditor';
import { useImageAttachments } from '../../../hooks/useImageAttachments';
import { refreshMarketConditions } from '../../../services/marketConditionsStore';
import { marketConditionDot, marketConditionNote, marketConditionTone, marketStatusLabel, type MarketConditionStatus } from '../../../utils/marketConditionStatus';
import { markets, refreshCapacity, saveMarketNote, useCapacityPreview } from '../capacity/model';
import { buttonClass, PreviewNotice, primaryClass } from '../capacity/CapacityForms';
import DatedMarketConditionsDrawer from '../../researching-reports/DatedMarketConditionsDrawer';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialMarket?: string;
}

// One label treatment for the status group and Days out, so they line up.
const fieldLabelClass = 'mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted';

/** 'YYYY-MM-DD' as a local calendar day, never shifted into UTC. */
function dayAsDate(day: string): Date | null {
  const parsed = new Date(`${day}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export default function MarketConditionsDrawer({ isOpen, onClose, initialMarket }: Props) {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date());
  const [day, setDay] = useState(today);
  useEffect(() => { if (isOpen) setDay(today); }, [isOpen, today]);
  return day === today
    ? <CurrentMarketConditionsDrawer isOpen={isOpen} onClose={onClose} initialMarket={initialMarket} day={day} onDayChange={setDay} />
    : <DatedMarketConditionsDrawer day={day} market={initialMarket ?? 'Houston'} markets={markets} isOpen={isOpen} onClose={onClose} onSaved={() => {}} onDayChange={setDay} />;
}

function CurrentMarketConditionsDrawer({ isOpen, onClose, initialMarket, day, onDayChange }: Props & { day: string; onDayChange: (day: string) => void }) {
  // Annotation uses the same ScreenshotEditor as bug reports; owned here so the
  // drawer works the same from the Scheduler page and from the header pill.
  const [editing, setEditing] = useState<number | null>(null);
  const inputClass = useCapacityInputClass();
  const { notes, previousDayConditions, loading, loaded, canEditDaysOut = false } = useCapacityPreview();
  const [market, setMarket] = useState(initialMarket ?? 'Houston');
  useEffect(() => { if (isOpen && initialMarket) setMarket(initialMarket); }, [isOpen, initialMarket]);
  // The capacity snapshot is fetched once when the shell mounts, and the socket
  // push only refreshes the pill's own store -- so a drawer opened later, or
  // after the dated editor wrote today's note, can hold a stale version and get
  // its save rejected with a conflict. Re-read the versions on open.
  useEffect(() => { if (isOpen) void refreshCapacity(); }, [isOpen]);
  // Keep each draft while switching markets or closing the drawer this session.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [draftVersions, setDraftVersions] = useState<Record<string, number>>({});
  // Images: new uploads live in the attachments hook for the current market and
  // are stashed per market on switch; removed saved images are tracked by id.
  const [newImageDrafts, setNewImageDrafts] = useState<Record<string, string[]>>({});
  const [removedImageIds, setRemovedImageIds] = useState<Record<string, number[]>>({});
  const [savedMarket, setSavedMarket] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [pendingAction, setPendingAction] = useState<{ kind: 'close' } | { kind: 'day'; day: string } | null>(null);
  const [dailyDrafts, setDailyDrafts] = useState<Record<string,{status:'green'|'yellow'|'red'; daysOut:number|null}>>({});
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date());
  const attachments = useImageAttachments({ active: isOpen });
  const note = notes[market];
  const conditionStatus = dailyDrafts[market]?.status ?? (note?.conditionDay === today ? note.conditionStatus : undefined) ?? 'green';
  const daysOut = dailyDrafts[market] ? dailyDrafts[market].daysOut : note?.daysOut ?? null;
  const text = drafts[market] ?? note?.text ?? '';
  const savedImages = (note?.images ?? []).filter(image => !(removedImageIds[market] ?? []).includes(image.id));
  const marketDirty = (name: string) => {
    const saved = notes[name];
    const draft = dailyDrafts[name];
    const newImages = name === market ? attachments.images : newImageDrafts[name] ?? [];
    return (drafts[name] ?? saved?.text ?? '') !== (saved?.text ?? '')
      || newImages.length > 0 || (removedImageIds[name]?.length ?? 0) > 0
      || (draft?.status ?? (saved?.conditionDay === today ? saved.conditionStatus : undefined) ?? 'green') !== (saved?.conditionDay === today ? saved.conditionStatus : 'green')
      || (draft ? draft.daysOut : saved?.daysOut ?? null) !== (saved?.daysOut ?? null);
  };
  const dirty = marketDirty(market);
  const anyDirty = markets.some(marketDirty);

  const discardAll = () => {
    setDrafts({});
    setDraftVersions({});
    setNewImageDrafts({});
    setRemovedImageIds({});
    setDailyDrafts({});
    attachments.reset();
    setSavedMarket('');
    setValidationError('');
  };
  const confirmDiscard = () => {
    const action = pendingAction;
    setPendingAction(null);
    discardAll();
    if (action?.kind === 'day') onDayChange(action.day);
    else if (action?.kind === 'close') onClose();
  };

  const switchMarket = (next: string) => {
    setNewImageDrafts(current => ({ ...current, [market]: attachments.images }));
    attachments.setImages(newImageDrafts[next] ?? []);
    attachments.setError(null);
    setValidationError('');
    setMarket(next);
  };
  const touch = () => {
    setDraftVersions(current => ({ ...current, [market]: current[market] ?? note?.version ?? 0 }));
    setSavedMarket('');
  };
  const copyFromPreviousDay = () => {
    const previous = previousDayConditions?.[market];
    if (!previous) return;
    setValidationError('');
    touch();
    setDrafts(current => ({ ...current, [market]: previous.conditions }));
    setDailyDrafts(current => ({ ...current, [market]: {
      status: previous.condition_status, daysOut: canEditDaysOut ? previous.days_out : daysOut,
    } }));
  };
  const save = async (value: string, images: string[], keepImageIds: number[]) => {
    if (saving) return;
    if (conditionStatus !== 'green' && !value.trim()) {
      setValidationError('Add a note before saving Yellow or Red market conditions.');
      return;
    }
    setValidationError('');
    setSaveError('');
    setSaving(true);
    try {
      await saveMarketNote(market, value, draftVersions[market] ?? note?.version ?? 0, images, keepImageIds, {condition_status: conditionStatus, days_out: daysOut});
      setDraftVersions(current => { const copy = { ...current }; delete copy[market]; return copy; });
      setDrafts(current => ({ ...current, [market]: value }));
      setNewImageDrafts(current => { const copy = { ...current }; delete copy[market]; return copy; });
      setRemovedImageIds(current => { const copy = { ...current }; delete copy[market]; return copy; });
      setDailyDrafts(current => { const copy = { ...current }; delete copy[market]; return copy; });
      attachments.reset();
      setSavedMarket(market);
      setHistoryKey(current => current + 1);
      // The header pill reads its own notes-only store; don't make the author wait for the socket echo.
      void refreshMarketConditions();
    } catch (error) {
      // Say why, next to the button that was pressed. The draft is kept: the
      // top-of-drawer notice is easy to miss in a drawer this tall, and a 403
      // from a missing write grant reads as "nothing happened" without this.
      setSaveError(error instanceof Error && error.message ? error.message : 'Could not save market conditions.');
    } finally {
      setSaving(false);
    }
  };

  return <Drawer isOpen={isOpen} onClose={onClose} onBeforeClose={() => { if (!anyDirty) return true; setPendingAction({ kind: 'close' }); return false; }} title="Market Conditions" maxWidth="full" width="min(96vw, 900px)" overlay={<WarningConfirmModal inPlace open={pendingAction !== null} title="Discard unsaved changes?" message={pendingAction?.kind === 'day' ? 'Changing the operating day will discard your unsaved market conditions.' : 'You have unsaved market conditions. Closing will discard them.'} confirmLabel={pendingAction?.kind === 'day' ? 'Discard & Change Day' : 'Discard & Close'} cancelLabel="Stay" onConfirm={confirmDiscard} onCancel={() => setPendingAction(null)} />}>
    {/* Only a failed initial LOAD may replace the editor -- never a failed
        save, which also sets the shared error and would otherwise wipe the
        form and the draft in it. Until a read succeeds there is nothing
        truthful to show: every market would render the green default, which
        says "Normal Operations" when the real answer is "unknown". */}
    {loading ? <div role="status" className="px-4 py-8 text-sm text-muted">Loading market conditions…</div>
     : !loaded ? <PreviewNotice /> : <>
    {!saveError && <PreviewNotice />}
    <div className="mb-4">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <DateTimePicker mode="date" labelType="custom" label="Operating day" ariaLabel="Operating day"
          value={dayAsDate(day)} usePortal autoFlip autoApply
          onChange={value => {
            const picked = calendarDate(value);
            if (!picked) return;
            const next = dateParam(picked);
            if (next === day) return;
            if (anyDirty) setPendingAction({ kind: 'day', day: next });
            else onDayChange(next);
          }} />
        <p className="text-xs text-muted">{note?.conditionDay === today ? `Updated ${new Date(note.updatedAt).toLocaleString()} by ${(note.author || '').split('@')[0]}` : 'Never updated today'}</p>
      </div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Select a market</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="tablist" aria-label="Market conditions">
        {markets.map(name => {
          const saved = notes[name];
          const status = dailyDrafts[name]?.status ?? (saved?.conditionDay === today ? saved.conditionStatus : undefined) ?? 'green';
          const preview = drafts[name] ?? saved?.text;
          const imageCount = (saved?.images?.length ?? 0) - (removedImageIds[name]?.length ?? 0) + (name === market ? attachments.images.length : newImageDrafts[name]?.length ?? 0);
          const pending = marketDirty(name);
          return <button key={name} type="button" role="tab" aria-selected={market === name} onClick={() => switchMarket(name)}
            className={`min-h-20 rounded-lg border p-2.5 text-left transition hover:shadow-sm ${status ? marketConditionTone[status] : 'border-theme-border-primary bg-theme-bg-primary text-heading'} ${market === name ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}>
            <span className="flex items-center justify-between gap-1"><span className="font-semibold">{name}</span><span className={`h-2.5 w-2.5 rounded-full ${status ? marketConditionDot[status] : 'bg-slate-300'}`} /></span>
            <span className="mt-1 block truncate text-xs opacity-75">{marketConditionNote(preview)}</span>
            {imageCount > 0 && <span className="mt-1 block text-xs font-medium" aria-label={`${imageCount} images attached`}>📷 {imageCount}</span>}
            {pending && <span className="mt-1 block text-xs font-bold text-amber-700 dark:text-amber-300">Not Saved</span>}
          </button>;
        })}
      </div>
    </div>
    <section className="space-y-3 rounded-xl border-2 border-blue-200 bg-theme-bg-primary p-4 dark:border-blue-800">
      <h3 className="text-lg font-semibold text-heading">Editing {market}</h3>
      {/* Both groups use the same label style and margin, and every control is
          h-9, so the two labels and the two rows of controls share a baseline. */}
      <div className="flex flex-wrap items-start gap-4 rounded-lg border border-theme-border-primary bg-theme-bg-secondary p-3">
        <div>
          <p id="market-status-label" className={fieldLabelClass}>Today's status</p>
          <div className="flex gap-1" role="group" aria-labelledby="market-status-label" aria-label={`${market} status`}>
            {(['green','yellow','red'] as MarketConditionStatus[]).map(status => <button key={status} type="button" aria-pressed={conditionStatus === status}
              onClick={() => { setValidationError(''); setDailyDrafts(current => ({...current, [market]: {status, daysOut}})); }}
              className={`h-9 rounded-md border px-3 text-sm font-medium ${marketConditionTone[status]} ${conditionStatus === status ? 'ring-2 ring-blue-500 ring-offset-1' : 'opacity-65'}`}>{marketStatusLabel(status)}</button>)}
          </div>
        </div>
        {/* Keep the field at the production size even though the shared input
            class includes w-full and generous vertical padding. */}
        <div className="mx-auto min-w-0 shrink-0" style={{ width: 72 }}>
          <label htmlFor="market-conditions-days-out" className={`block whitespace-nowrap ${fieldLabelClass}`}>Days out</label>
          <input id="market-conditions-days-out" className={`${inputClass} min-w-0 disabled:opacity-60`} style={{ width: 72, height: 44, padding: '8px 12px', boxSizing: 'border-box' }} type="number" min="0" max="365" step="0.5" value={daysOut ?? ''} disabled={!canEditDaysOut}
            onChange={e=>setDailyDrafts(current=>({...current,[market]:{status:conditionStatus,daysOut:e.target.value === '' ? null : Number(e.target.value)}}))} />
        </div>
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <label htmlFor="market-conditions-note" className="block text-sm font-medium text-label">{market} market conditions{conditionStatus !== 'green' && <span aria-hidden="true" className="text-red-600"> *</span>}</label>
          <button type="button" aria-expanded={showHistory} onClick={() => setShowHistory(current => !current)} className="rounded px-1.5 py-0.5 text-xs font-medium text-blue-600 underline hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950">History</button>
        </div>
        <textarea id="market-conditions-note" className={inputClass} rows={5} value={text} placeholder="Normal Operations" aria-label={`${market} market conditions`} aria-required={conditionStatus !== 'green'} aria-invalid={!!validationError} onChange={e => {
          setValidationError('');
          touch();
          setDrafts(current => ({ ...current, [market]: e.target.value }));
        }} />
      </div>
      {showHistory && <MarketConditionsHistory market={market} day={day} refreshKey={historyKey} onClose={() => setShowHistory(false)} />}
      {validationError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{validationError}</p>}
      <details className="rounded-lg border border-theme-border-primary p-3" open={savedImages.length > 0 || attachments.images.length > 0}>
        <summary className="cursor-pointer text-sm font-medium text-label">Screenshots ({savedImages.length + attachments.images.length})</summary>
        <div className="mt-3">
        <p className="mb-2 text-xs text-muted">Paste an image from your clipboard (Ctrl+V / Cmd+V) or upload PNG, JPG, or GIF. Up to 5 per market.</p>
        <label className={`${buttonClass} cursor-pointer`}>
          Upload images
          <input aria-label="Upload images" type="file" multiple accept="image/png,image/jpeg,image/jpg,image/gif" className="hidden" onChange={e => { touch(); attachments.handleFileInput(e); }} />
        </label>
        {attachments.error && <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">{attachments.error}</p>}
        {(savedImages.length > 0 || attachments.images.length > 0) && <div className="mt-3 grid grid-cols-3 gap-3">
          {savedImages.map(image => <MarketConditionsImageThumb key={image.id} id={image.id} name={image.name} onRemove={() => {
            touch();
            setRemovedImageIds(current => ({ ...current, [market]: [...(current[market] ?? []), image.id] }));
          }} />)}
          {attachments.images.map((image, index) => <div key={index} className="relative">
            <img src={image} alt={`New image ${index + 1}`} title="Click to annotate" className="h-24 w-full cursor-pointer rounded-md border border-gray-300 object-cover hover:opacity-90 dark:border-gray-600" onClick={() => setEditing(index)} />
            <button type="button" aria-label={`Remove new image ${index + 1}`} onClick={() => attachments.removeImage(index)} className="absolute right-1 top-1 rounded-full bg-red-600 p-1 text-white hover:bg-red-700">
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>)}
        </div>}
        </div>
      </details>
      <p className="text-sm text-muted">These conditions — status, days out, notes and screenshots — appear at the top of capacity request forms for {market} and in the Market Conditions pill for every user. Saving sends everyone a toast.</p>
      {dirty && <p className="text-xs font-bold text-amber-700 dark:text-amber-300">Not Saved</p>}
      <div className="flex gap-2">
        <button className={`${primaryClass} disabled:cursor-not-allowed disabled:opacity-50`} disabled={saving} onClick={() => save(text, attachments.images, savedImages.map(image => image.id))}>{saving ? 'Saving…' : 'Save'}</button>
        <button className={buttonClass} onClick={() => { touch(); setDrafts(current => ({ ...current, [market]: '' })); }}>Clear</button>
        <button className={`${buttonClass} disabled:cursor-not-allowed disabled:opacity-50`} disabled={!previousDayConditions?.[market]} onClick={copyFromPreviousDay}>Copy from previous day</button>
      </div>
      {saveError && <p role="alert" className="rounded border border-red-400 p-2 text-sm text-red-600 dark:text-red-400">{saveError}</p>}
      {savedMarket === market && !dirty && <p role="status" className="text-sm text-green-700 dark:text-green-300">Market conditions saved.</p>}
    </section>
    {editing !== null && attachments.images[editing] && <ScreenshotEditor
      isOpen
      imageData={attachments.images[editing]}
      onClose={() => setEditing(null)}
      onApply={next => { attachments.updateImage(editing, next); setEditing(null); }}
    />}
    </>}
  </Drawer>;
}
